import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Package, Clock, AlertCircle, Truck, RefreshCcw, ArrowRight, Copy } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_MAP = {
  created: { label: "Awaiting Payment", icon: Clock, color: "text-amber-600", bg: "bg-amber-50" },
  paid: { label: "Payment Successful", icon: CheckCircle2, color: "text-green-600", bg: "bg-green-50" },
  shipped: { label: "Shipped", icon: Truck, color: "text-blue-600", bg: "bg-blue-50" },
  delivered: { label: "Delivered", icon: Package, color: "text-green-700", bg: "bg-green-50" },
  failed: { label: "Payment Failed", icon: AlertCircle, color: "text-red-600", bg: "bg-red-50" },
  refunded: { label: "Refunded", icon: RefreshCcw, color: "text-neutral-600", bg: "bg-neutral-100" },
  refund_initiated: { label: "Refund Initiated", icon: RefreshCcw, color: "text-amber-600", bg: "bg-amber-50" },
  disputed: { label: "Under Dispute", icon: AlertCircle, color: "text-red-700", bg: "bg-red-50" },
  signature_failed: { label: "Verification Failed", icon: AlertCircle, color: "text-red-700", bg: "bg-red-50" },
};

export default function OrderSuccess() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchOrder = async () => {
      try {
        const { data } = await axios.get(`${API}/orders/${id}`);
        if (!cancelled) setOrder(data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchOrder();
    return () => { cancelled = true; };
  }, [id]);

  if (loading) {
    return (
      <main className="max-w-3xl mx-auto px-4 py-20 text-center">
        <div className="animate-pulse text-neutral-500">Loading order details...</div>
      </main>
    );
  }

  if (error || !order) {
    return (
      <main className="max-w-3xl mx-auto px-4 py-20 text-center" data-testid="order-not-found">
        <AlertCircle className="w-12 h-12 text-red-500 mx-auto" />
        <h1 className="mt-4 font-display text-3xl font-black">Order not found</h1>
        <p className="mt-2 text-neutral-500">{error || "We couldn't find this order."}</p>
        <Link to="/shop" className="mt-6 inline-block text-[#FF5A1F] font-semibold">← Back to shop</Link>
      </main>
    );
  }

  const status = STATUS_MAP[order.status] || STATUS_MAP.created;
  const StatusIcon = status.icon;
  const isSuccess = order.status === "paid" || order.status === "shipped" || order.status === "delivered";

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  return (
    <main className="max-w-3xl mx-auto px-4 md:px-8 py-8 md:py-14" data-testid="order-success-page">
      {/* Status banner */}
      <div className={`${status.bg} rounded-3xl p-6 md:p-10 text-center`}>
        <div className={`w-14 h-14 md:w-16 md:h-16 rounded-full bg-white mx-auto flex items-center justify-center ${status.color}`}>
          <StatusIcon className="w-7 h-7 md:w-8 md:h-8" />
        </div>
        <h1 className="mt-4 md:mt-5 font-display text-2xl md:text-4xl font-black text-neutral-900 leading-tight" data-testid="order-status-label">
          {isSuccess ? "Thank you for your order!" : status.label}
        </h1>
        {isSuccess && (
          <p className="mt-2 text-sm md:text-base text-neutral-700 break-words">A confirmation has been sent to <span className="font-semibold">{order.customer_email}</span></p>
        )}
        <div className="mt-5 md:mt-6 inline-flex items-center gap-2 px-3 md:px-4 py-2 rounded-full bg-white text-xs md:text-sm font-mono max-w-full">
          <span className="text-neutral-500 whitespace-nowrap">Order ID:</span>
          <span className="font-semibold truncate" data-testid="order-id">{order.razorpay_order_id}</span>
          <button onClick={() => copy(order.razorpay_order_id, "Order ID")} className="text-neutral-500 hover:text-neutral-900 flex-shrink-0">
            <Copy className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Order details */}
      <div className="mt-6 md:mt-8 grid md:grid-cols-2 gap-4 md:gap-6">
        <div className="bg-neutral-50 rounded-2xl p-5 md:p-6">
          <h3 className="text-[10px] md:text-xs font-bold uppercase tracking-[0.2em] text-neutral-500">Shipping To</h3>
          <div className="mt-3 text-sm text-neutral-900">
            <div className="font-semibold">{order.customer_name}</div>
            <div className="text-neutral-600 mt-1">{order.customer?.addr1}</div>
            {order.customer?.addr2 && <div className="text-neutral-600">{order.customer.addr2}</div>}
            <div className="text-neutral-600">{order.customer?.city}, {order.customer?.state} {order.customer?.pin}</div>
            <div className="text-neutral-600 mt-2">{order.customer_phone}</div>
          </div>
        </div>
        <div className="bg-neutral-50 rounded-2xl p-5 md:p-6">
          <h3 className="text-[10px] md:text-xs font-bold uppercase tracking-[0.2em] text-neutral-500">Payment</h3>
          <div className="mt-3 text-sm space-y-1">
            <div className="flex justify-between gap-2"><span className="text-neutral-600">Status</span><span className={`font-semibold ${status.color} text-right`}>{status.label}</span></div>
            <div className="flex justify-between"><span className="text-neutral-600">Amount</span><span className="font-semibold">₹{order.amount_inr}</span></div>
            {order.razorpay_payment_id && (
              <div className="flex justify-between gap-2">
                <span className="text-neutral-600 flex-shrink-0">Payment ID</span>
                <span className="font-mono text-[10px] md:text-xs truncate" title={order.razorpay_payment_id}>{order.razorpay_payment_id}</span>
              </div>
            )}
            {order.tracking_number && (
              <div className="flex justify-between gap-2 pt-2 border-t border-neutral-200 mt-2">
                <span className="text-neutral-600 flex-shrink-0">Tracking</span>
                <span className="font-semibold text-right">{order.courier} · {order.tracking_number}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Items */}
      <div className="mt-6 bg-white border border-neutral-200 rounded-2xl p-5 md:p-6">
        <h3 className="font-display text-base md:text-lg font-black mb-4">Items ({order.items?.length || 0})</h3>
        <div className="space-y-3">
          {(order.items || []).map((item) => (
            <div key={item.id} className="flex items-center justify-between gap-3 md:gap-4 py-2 border-b border-neutral-100 last:border-0">
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm text-neutral-900 line-clamp-1">{item.title}</div>
                <div className="text-xs text-neutral-500 mt-0.5">Qty {item.qty} × ₹{item.sale}</div>
              </div>
              <div className="font-semibold text-sm flex-shrink-0">₹{item.qty * item.sale}</div>
            </div>
          ))}
        </div>
        <div className="mt-4 pt-4 border-t border-neutral-200 flex justify-between items-baseline">
          <span className="font-bold">Total Paid</span>
          <span className="font-display text-xl md:text-2xl font-black">₹{order.amount_inr}</span>
        </div>
      </div>

      <div className="mt-6 md:mt-8 flex flex-col sm:flex-row flex-wrap gap-3 sm:justify-center">
        <Link to="/orders" className="h-12 px-6 md:px-7 rounded-full border-2 border-neutral-900 font-bold text-sm flex items-center justify-center hover:bg-neutral-900 hover:text-white transition" data-testid="view-orders-btn">
          View All My Orders
        </Link>
        <Link to="/shop" className="h-12 px-6 md:px-7 rounded-full bg-[#FF5A1F] hover:bg-neutral-900 text-white font-bold text-sm flex items-center justify-center gap-2 transition">
          Continue Shopping <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </main>
  );
}
